import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * usePersistedTab — Hook pour gérer un onglet actif avec persistance localStorage + deep-link.
 *
 * Priorité de résolution du tab initial :
 *   1. Query string `?tab=...` (deep-link externe) si dans `validValues`
 *   2. localStorage[storageKey] si dans `validValues`
 *   3. defaultValue
 *
 * @param storageKey   Clé localStorage (ex: 'homly_mandate_detail_tab')
 * @param defaultValue Valeur par défaut (ex: 'apercu')
 * @param validValues  Liste des valeurs valides (whitelist)
 * @returns [activeTab, setActiveTab]
 */
export default function usePersistedTab(storageKey, defaultValue, validValues = null) {
    const [searchParams] = useSearchParams();

    const resolveInitial = () => {
        // 1. Deep-link
        const fromQuery = searchParams.get('tab');
        if (fromQuery && (!validValues || validValues.includes(fromQuery))) {
            return fromQuery;
        }
        // 2. localStorage
        try {
            const stored = localStorage.getItem(storageKey);
            if (stored && (!validValues || validValues.includes(stored))) {
                return stored;
            }
        } catch { /* localStorage indisponible */ }
        // 3. Default
        return defaultValue;
    };

    const [activeTab, _setActiveTab] = useState(resolveInitial);

    const setActiveTab = useCallback((value) => {
        _setActiveTab(value);
        try {
            localStorage.setItem(storageKey, value);
        } catch { /* quota / mode privé */ }
    }, [storageKey]);

    // Si l'URL change (ex: changement de mandat), on re-résout
    useEffect(() => {
        const fromQuery = searchParams.get('tab');
        if (fromQuery && (!validValues || validValues.includes(fromQuery))) {
            _setActiveTab(fromQuery);
            try { localStorage.setItem(storageKey, fromQuery); } catch { /* quota */ }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    return [activeTab, setActiveTab];
}
